/**
 * <juro-globe>: a dotted globe with flight arcs out of Denmark
 * ------------------------------------------------------------------
 * Adapted from "COBE Globe" and "Globe Flights" on 21st.dev (shuding): a
 * sphere of dots, turned so Europe faces the viewer, with arcs lifting off
 * from Denmark to cities across Europe.
 *
 * It rocks gently rather than spinning, so Europe never turns away.
 *
 * Canvas 2D, no WebGL, no library. The dots are a Fibonacci sphere computed
 * once; each frame only rotates and projects them. Stops offscreen and when
 * the tab is hidden, capped at 30fps, drawn once under reduced motion.
 *
 * Attribute: data-color, the dot/arc colour as a hex, default #0f1b2c.
 * Decorative: aria-hidden. Nothing but pixels is written.
 */

(() => {
  if (customElements.get('juro-globe')) return;

  const DEG = Math.PI / 180;
  const ORIGIN = [56.2, 9.5]; // Denmark
  const DESTINATIONS = [
    [40.4, -3.7], // Madrid
    [48.9, 2.35], // Paris
    [52.5, 13.4], // Berlin
    [59.3, 18.1], // Stockholm
    [45.5, 9.2], // Milan
    [52.4, 4.9], // Amsterdam
    [51.5, -0.1], // London
    [38.7, -9.1], // Lisbon
  ];
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Latitude/longitude to a unit vector. */
  const toVec = (lat, lon) => {
    const phi = lat * DEG;
    const lambda = lon * DEG;
    return [Math.cos(phi) * Math.sin(lambda), Math.sin(phi), Math.cos(phi) * Math.cos(lambda)];
  };

  /** Spherical interpolation between two unit vectors. */
  const slerp = (a, b, t) => {
    const dot = Math.min(1, Math.max(-1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    const omega = Math.acos(dot);
    if (omega < 1e-6) return a;
    const s = Math.sin(omega);
    const wa = Math.sin((1 - t) * omega) / s;
    const wb = Math.sin(t * omega) / s;
    return [a[0] * wa + b[0] * wb, a[1] * wa + b[1] * wb, a[2] * wa + b[2] * wb];
  };


  /*
   * Simplified coastlines as [lon, lat] rings, hand-traced to roughly 1°
   * accuracy: continental Europe, Iberia, Scandinavia and Finland, Great
   * Britain, Ireland, North Africa and Anatolia. Enough for the dots to read
   * as Europe on a globe, which is all a decorative figure needs, and small
   * enough to ship inline instead of loading a map file.
   */
  const LAND = [
    [[-9.5,43.5],[-8.8,42],[-9,39],[-8.9,37],[-6,36.2],[-5.3,36],[-2,36.7],[0,38.5],[0.2,39.5],[3.2,41.9],[3.1,42.5],[-1.8,43.4],[-4.5,43.4],[-8,43.7]],
    [[-1.8,43.4],[-1.2,46.2],[-4.5,48.4],[-1.6,49.6],[1.6,50.9],[3.5,51.4],[4.7,52.9],[7,53.6],[8.6,53.9],[8.6,55.5],[8.1,56.8],[10.5,57.7],[10.6,56.4],[12.5,55.6],[10.9,54.1],[14.2,53.9],[18.6,54.6],[21.1,55.7],[24,57.2],[28,59.5],[30,60],[40,60],[40,45],[37,46.5],[34,45],[30.5,46.5],[28.7,44],[28,41.5],[26,40.5],[24,40.5],[22.9,40.6],[24,38],[22.5,36.5],[21.1,37.8],[19.4,40.4],[19.4,42],[16,43.5],[13.7,45.1],[12.3,45.3],[12.3,44.2],[13.6,43.5],[16,41.9],[18.5,40.2],[17,39],[16.1,38],[15.6,38.2],[15.7,40],[13.8,41.2],[11.1,42.4],[10.2,43.9],[8.8,44.4],[7.5,43.8],[4.8,43.3],[3.1,42.5]],
    [[5,58],[5.2,60.4],[4.9,61.8],[7,62.8],[10,64],[13,66],[15.5,68.5],[18.7,69.7],[23.6,70.6],[28,71],[31,70],[29,69],[28.5,67],[30,65],[30,62],[29,61],[27,60.5],[23,60],[21.4,60.9],[21.5,62.6],[25.3,65.1],[22.3,65.8],[21.2,64.8],[19,63.5],[17.5,62.3],[17.2,60.7],[18.8,59.8],[18,59.2],[16.5,57],[16,56.2],[14.2,55.4],[12.9,55.6],[12.6,56.2],[11.9,57.7],[11.2,58.9],[10.5,59.4],[8,58.1],[5.6,58.1]],
    [[-5.7,50],[1.4,51.2],[1.7,52.7],[0.3,53.5],[-1.5,55],[-2,55.9],[-1.8,57.5],[-3,58.6],[-5,58.6],[-6.2,56.8],[-5.6,55.3],[-3,54.3],[-4.6,53.3],[-4.6,52.1],[-5.3,51.7],[-3,51.2]],
    [[-6,52],[-6.2,53.9],[-5.5,55.2],[-7.3,55.4],[-8.5,54.5],[-10,53.5],[-10.3,51.9],[-8.5,51.6]],
    [[-17,21],[-17,27],[-10,29.5],[-9.8,32],[-6.8,34],[-5.9,35.8],[-2,35.1],[3,36.8],[10,37.3],[11,35],[10,33.5],[15,32.4],[20,30.5],[20,32.8],[23,32.6],[29,31],[32.3,31.3],[35,30],[40,20],[-17,20]],
    [[26,40.2],[26.5,38.4],[28,36.7],[36,36.2],[35.9,34],[35,32],[35,30],[40,30],[44,37],[42,41.5],[36.5,41.3],[33,42],[29,41.2]]
  ];

  /** Ray-casting point-in-polygon on [lon, lat]. */
  const inRing = (lon, lat, ring) => {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  };

  /** Land dots on a roughly even grid across the region the globe shows. */
  const buildLand = () => {
    const out = [];
    const step = 0.72;
    for (let lat = 18; lat <= 72; lat += step) {
      const lonStep = step / Math.cos(lat * DEG);
      for (let lon = -20; lon <= 45; lon += lonStep) {
        if (LAND.some((ring) => inRing(lon, lat, ring))) out.push(toVec(lat, lon));
      }
    }
    return out;
  };

  const hexToRgb = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    const n = parseInt(m ? m[1] : '0f1b2c', 16);
    return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
  };

  class JuroGlobe extends HTMLElement {
    connectedCallback() {
      if (this.canvas) return;
      this.setAttribute('aria-hidden', 'true');
      this.canvas = document.createElement('canvas');
      this.appendChild(this.canvas);
      this.ctx = this.canvas.getContext('2d');
      if (!this.ctx) return;

      this.rgb = hexToRgb(this.dataset.color);
      this.dots = this.buildDots(3000);
      this.land = buildLand();
      this.origin = toVec(...ORIGIN);
      this.arcs = DESTINATIONS.map((d, i) => ({ to: toVec(...d), offset: i / DESTINATIONS.length }));

      this.visible = false;
      this.running = false;
      this.last = 0;
      this.frame = this.frame.bind(this);
      this.sync = this.sync.bind(this);

      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);
      this.io = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.sync();
      });
      this.io.observe(this);
      document.addEventListener('visibilitychange', this.sync);
      this.resize();
    }

    disconnectedCallback() {
      this.running = false;
      cancelAnimationFrame(this.raf);
      this.resizeObserver?.disconnect();
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.sync);
    }

    buildDots(count) {
      const dots = [];
      const golden = Math.PI * (3 - Math.sqrt(5));
      for (let i = 0; i < count; i++) {
        const y = 1 - (i / (count - 1)) * 2;
        const r = Math.sqrt(1 - y * y);
        const theta = golden * i;
        dots.push([Math.cos(theta) * r, y, Math.sin(theta) * r]);
      }
      return dots;
    }

    resize() {
      const rect = this.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      this.size = Math.max(1, Math.min(rect.width, rect.height));
      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.w = rect.width;
      this.h = rect.height;
      this.draw(0);
    }

    sync() {
      const run = this.visible && !document.hidden && !reduced.matches;
      if (run && !this.running) {
        this.running = true;
        this.raf = requestAnimationFrame(this.frame);
      } else if (!run && this.running) {
        this.running = false;
        cancelAnimationFrame(this.raf);
      }
    }

    frame(now) {
      if (!this.running) return;
      this.raf = requestAnimationFrame(this.frame);
      if (now - this.last < 33) return;
      this.last = now;
      this.draw(now);
    }

    /** Rotate: yaw (around y) then a fixed pitch so Europe sits slightly above centre. */
    project(v, yaw, pitch) {
      const cy = Math.cos(yaw);
      const sy = Math.sin(yaw);
      const x1 = v[0] * cy - v[2] * sy;
      const z1 = v[0] * sy + v[2] * cy;
      const cp = Math.cos(pitch);
      const sp = Math.sin(pitch);
      const y2 = v[1] * cp - z1 * sp;
      const z2 = v[1] * sp + z1 * cp;
      // Zoomed in: the sphere is almost as wide as the stage and its centre sits
      // low, so the top of the planet (and Europe on it) fills the frame and
      // the stage crops the rest.
      const r = this.size * 1.3;
      return [this.w / 2 + x1 * r, this.h * 0.62 - y2 * r, z2];
    }

    draw(now) {
      const { ctx } = this;
      if (!ctx || !this.w) return;
      const t = now / 1000;
      // Face Denmark's longitude, rock ±7° around it.
      const yaw = (ORIGIN[1] + Math.sin(t * 0.25) * 7) * DEG;
      const pitch = 50 * DEG; // tips the north towards the viewer so Europe faces forward

      ctx.clearRect(0, 0, this.w, this.h);

      // Dots: front bright, back faint.
      const back = new Path2D();
      const front = new Path2D();
      const dotR = Math.max(0.8, this.size / 700);
      for (const d of this.dots) {
        const [x, y, z] = this.project(d, yaw, pitch);
        const path = z > 0 ? front : back;
        const r = z > 0 ? dotR * (0.7 + z * 0.5) : dotR * 0.7;
        path.moveTo(x + r, y);
        path.arc(x, y, r, 0, Math.PI * 2);
      }
      ctx.fillStyle = `rgba(${this.rgb}, 0.05)`;
      ctx.fill(back);
      ctx.fillStyle = `rgba(${this.rgb}, 0.16)`;
      ctx.fill(front);

      // Land: denser, darker dots, so the continents read against the sea.
      const land = new Path2D();
      const landR = Math.max(1, this.size / 560);
      for (const d of this.land) {
        const [x, y, z] = this.project(d, yaw, pitch);
        if (z <= 0.02) continue;
        land.moveTo(x + landR, y);
        land.arc(x, y, landR, 0, Math.PI * 2);
      }
      ctx.fillStyle = `rgba(${this.rgb}, 0.62)`;
      ctx.fill(land);

      // Arcs out of Denmark, each with a pulse travelling along it.
      const accent = '30, 111, 232';
      for (const arc of this.arcs) {
        const points = [];
        for (let i = 0; i <= 40; i++) {
          const s = i / 40;
          const v = slerp(this.origin, arc.to, s);
          const lift = 1 + Math.sin(s * Math.PI) * 0.14;
          points.push(this.project([v[0] * lift, v[1] * lift, v[2] * lift], yaw, pitch));
        }
        ctx.beginPath();
        points.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
        ctx.strokeStyle = `rgba(${accent}, 0.35)`;
        ctx.lineWidth = 1.2;
        ctx.stroke();

        const phase = reduced.matches ? 0.6 : (t * 0.35 + arc.offset) % 1;
        const head = Math.floor(phase * 40);
        const tail = Math.max(0, head - 8);
        ctx.beginPath();
        for (let i = tail; i <= head; i++) {
          const [x, y] = points[i];
          i === tail ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${accent}, 0.95)`;
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();

        const [ex, ey] = points[40];
        ctx.beginPath();
        ctx.arc(ex, ey, 2.4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${accent}, 0.9)`;
        ctx.fill();
      }

      // Denmark: a pulsing marker.
      const [ox, oy] = this.project(this.origin, yaw, pitch);
      const pulse = reduced.matches ? 0.5 : (t * 0.8) % 1;
      ctx.beginPath();
      ctx.arc(ox, oy, 4 + pulse * 14, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(200, 16, 46, ${0.6 * (1 - pulse)})`;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(ox, oy, 4.5, 0, Math.PI * 2);
      ctx.fillStyle = '#C8102E';
      ctx.fill();
    }
  }

  customElements.define('juro-globe', JuroGlobe);
})();
